import type { FileManagerPermission } from '@/composeables/file-manager-capabilities';
import type { MountTarget } from '@/services/sharepoint-drive/sharepoint-drive-service';
import type { Audience } from 'cesg-shared/dist/models';
import {
  computed,
  inject,
  provide,
  toValue,
  watch,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter,
} from 'vue';
import type { SyntheticItem } from '../../types';

/**
 * A backing store grafted into the file manager's path namespace at `prefix` — the entirety of what
 * the file manager knows about where the content it browses actually lives.
 *
 * Built by the host and handed in as one inbound prop (D4). Nothing in here knows what a project, a
 * SharePoint site or a provisioning ledger is, which is what makes "many projects at the root" fall
 * out of prefix *depth* (`/1234 - Foo/CESG` instead of `/CESG`) with no new concept inside the
 * component: matchMountPath below is a longest-prefix match, not a first-segment split.
 */
export interface FileManagerMount {
  /**
   * The host's identity for this mount — opaque in here, only ever compared for equality (see
   * transferPolicyFor and evaluateDrop). Must be unique across the WHOLE table; that is a term of the
   * contract this file cannot check for itself. Two mounts sharing a key read as "the same mount", so
   * a drop between them would be attempted as a same-drive move (D8).
   */
  key: string;
  /**
   * Where this mount is grafted in — and, since the last segment doubles as the row's display name,
   * what the user reads. One segment on the project page (`/CESG`), two on the admin page
   * (`/1234 - Foo/CESG`). The host is responsible for sanitizing '/' out of every segment it builds
   * this from, since a stray slash would silently invent a namespace level.
   *
   * `'/'` itself is legal and means this mount IS the root: the pane opens directly inside the drive,
   * with no row for the mount anywhere and nothing above it to navigate back to. What a host uses
   * when a user can reach exactly one library and a one-row listing would be pure friction (see
   * `collapseSoleMount`).
   *
   * A root mount MUST then be the table's only mount — a term of the contract this file cannot check
   * for itself. The root's listing comes from the drive rather than from the table, so any other
   * mount would have no row anywhere in the tree or the grid and be reachable only by deep link.
   *
   * Must ALSO be unique across the whole table, for the same reason `key` must: two mounts sharing a
   * prefix make matchMountPath pick between them by sort order alone, and syntheticListing's `seen`
   * set collapses them into a single row — so one project's library silently SHADOWS another's, with
   * no error anywhere. Unreachable today only because deriveSiteTitle puts the project number first
   * in every title, i.e. the guarantee lives in the host's title format rather than in this contract.
   * provideFileManagerMounts console.errors a violating table in dev; it cannot do more than that.
   *
   * Worth being precise about whose property that is: uniqueness is a property of the NAMESPACE, not
   * of whatever backs each mount — two mounts can no more share a path than two folders can share a
   * name, whichever store answers for either of them. So the durable fix is not a stricter check in
   * here but a host that DECLARES its top-level containers and grafts each mount under one of them:
   * the prefixes are then unique by construction, and this paragraph becomes a note about how it used
   * to be guaranteed. Nothing below implements that; it only says where it belongs.
   */
  prefix: string;
  /**
   * The one provider-specific field on this otherwise source-agnostic abstraction: everything else
   * here (`prefix`, `audience`, `permissions`, `key`) describes a place in the namespace and who may
   * do what there, while this names a SharePoint drive. A mount backed by something else — the
   * server's project list, say — is this same record with this field replaced, which is why the
   * predicates below are careful to ask whether *anything* backs a path rather than whether a drive
   * does. Follow it to resolveMountPath and from there to the one listing call site
   * (useDirectoryChildrenQuery) to see the whole extent of the coupling.
   */
  driveId: string;
  /**
   * The drive's root driveItem id, when the host learned it (its accessibility probe returns it).
   * Absent for an unprobed mount, or one whose probe failed with something other than 403/404 — the
   * mount is still listed and still navigable in that case, but a drop onto its root then has to
   * resolve the reference the long way (getItemReference) instead of using the id it would otherwise
   * already have.
   */
  rootId?: string;
  // Which role families can reach this mount. Read by transferPolicyFor
  // (logic/transfer/transferPolicy.ts): the widening a cross-mount drop causes is the difference
  // between the two mounts' audiences, which is what decides whether the copy needs a disclosure
  // confirmation. Deliberately typed against cesg-shared rather than left opaque — TransferConfirmModal
  // renders it through a `Record<Audience, key>` specifically so a new audience is a compile error
  // instead of a raw i18n key appearing in the disclosure sentence (D7).
  audience: Audience[];
  /**
   * What this user may do inside this mount, computed by the host (D6). Opaque tokens in here — only
   * `permissions/modes.ts` interprets them, and only into UI states.
   *
   * Per mount rather than per pane, because the pane can now show several projects at once and a user's
   * `files` level is per project. Every read site therefore asks `permissionsAt(somePath)` rather than
   * a single ambient `permissions` ref.
   */
  permissions: FileManagerPermission[];
}

/**
 * A virtual path or mount prefix as its meaningful segments — the unit every comparison below is
 * expressed in, rather than substrings.
 *
 * That is what keeps `'/'` from being a special case: it is the one path whose separator and content
 * are the same character, so `prefix + '/'` and `path.slice(prefix.length)` each go wrong by exactly
 * one character against it. As a segment list the root is simply `[]`, and a mount grafted there
 * matches every path as the identity element. It also makes a partial-segment match (`/CESGX`
 * against `/CESG`) impossible by construction rather than by remembering to append a separator.
 */
const segmentsOf = (path: string): string[] => path.split('/').filter(Boolean);

/**
 * Pure: what to call a mount in the UI — the last segment of its prefix, never a separate `title`
 * field. One string carrying both structure and label is what keeps the two from disagreeing (D1),
 * and it means a mount grafted deeper (`/1234 - Foo/CESG`) still reads as plain `CESG` wherever a
 * name is wanted (the root row, the transfer confirmation's destination).
 *
 * `''` for a mount grafted at the root, which has no segment to name. Unreachable rather than
 * handled: a root mount is the table's only mount (see `prefix`), and the only readers left are the
 * CROSS-mount transfer confirmation and a mount row the root no longer has.
 */
export const mountDisplayName = (mount: FileManagerMount): string =>
  segmentsOf(mount.prefix).pop() ?? '';

/**
 * Pure, exported for tests: which mount a virtual file-manager path lives under, by longest-prefix
 * match over the mount table rather than splitting the path's first segment. With depth-1 mounts this
 * is behaviourally identical to a first-segment split — that is the property that made it a safe
 * refactor — and it is also what makes the admin page's multi-segment prefixes work unchanged.
 *
 * Undefined for the virtual root `/` (not under any mount — it is a synthetic listing of the mounts
 * themselves), for an intermediate namespace path, and for any path that is not under one of the
 * mounts the host supplied — which is what makes an inaccessible or unknown mount unnavigable rather
 * than a 403 waiting to happen.
 *
 * Both exceptions vanish for a table holding a mount at the root (`prefix: '/'`, i.e. no segments):
 * an empty prefix is a prefix of everything, so every path resolves and nothing is ever a namespace.
 * That is deliberate — the root then IS a drive — and it is what makes useNavigationPathGuard a
 * no-op for such a table: a stale deep link surfaces as a Graph 404 in FolderLoadError instead of a
 * silent redirect.
 */
export const matchMountPath = (
  path: string,
  mounts: FileManagerMount[],
): FileManagerMount | undefined => {
  const target = segmentsOf(path);
  return mounts
    .filter((mount) =>
      segmentsOf(mount.prefix).every(
        (segment, index) => target[index] === segment,
      ),
    )
    .sort(
      (a, b) => segmentsOf(b.prefix).length - segmentsOf(a.prefix).length,
    )[0];
};

/**
 * Pure, exported for tests: the same match as matchMountPath above, projected onto the drive
 * coordinates the Graph service consumes. Kept as a distinct entry point because the two callers
 * want genuinely different things — path-addressed Graph calls want {driveId, rest, virtual}, while
 * the drop policy wants the mount itself (its `audience`) and would otherwise have to look the mount
 * back up from a driveId it was just handed.
 *
 * This projection is the provider-specific half of the pair, and deliberately the narrower one: a
 * MountTarget is Graph's vocabulary (see `driveId`), so a mount backed by another source would need a
 * different projection while matchMountPath above would be unchanged. Which is why every question of
 * the form "is this path a place / can it be written to" goes through matchMountPath (mountAt,
 * hasBackingStore) and only the calls that actually hand coordinates to a service come through here.
 */
export const resolveMountPath = (
  path: string,
  mounts: FileManagerMount[],
): MountTarget | undefined => {
  const match = matchMountPath(path, mounts);
  if (!match) return undefined;

  // Segment arithmetic, not `path.slice(prefix.length)`: for a mount at the root that slice would
  // eat the leading separator (`/Ritningar` -> `Ritningar`), and buildDriveEndpoint would emit
  // `root:Ritningar:/children` — malformed, so every Graph call below the root would fail.
  const rest = segmentsOf(path).slice(segmentsOf(match.prefix).length);

  return {
    driveId: match.driveId,
    rest: rest.length ? `/${rest.join('/')}` : '/',
    virtual: path,
  };
};

/**
 * Pure, exported for tests: is this mount grafted STRICTLY below `base`, i.e. does it put at least
 * one row under that path. Segment-shaped rather than `(path, mounts)`-shaped like its neighbours,
 * because both callers have already split their path and re-splitting it per mount would turn a
 * table scan into a table scan with a string parse per row.
 *
 * Two conditions, and both are load-bearing. Deep enough to HAVE a next segment (`>` not `>=`):
 * without it a mount would list itself as its own child, so a mount root would never stop being a
 * synthetic listing and its real drive children would never be fetched. And actually descended from
 * `base`, compared segment by segment, which is what keeps `/1234` from matching `/1234 - Foo/CESG`.
 *
 * Written once because the whole synthetic model rests on this single structural rule: syntheticListing
 * materializes the rows it identifies, hasMountsBelow only counts them, and the two disagreeing would
 * mean a path that claims to be synthetic and then lists nothing (or the reverse).
 */
export const isMountBelow = (base: string[], prefix: string[]): boolean =>
  prefix.length > base.length &&
  base.every((segment, index) => prefix[index] === segment);

/**
 * Pure, exported for tests: does any mount sit below this path — the boolean half of syntheticListing,
 * without materializing the listing. Which is what makes it usable in a predicate that runs on every
 * navigation and every `enabled` re-evaluation, rather than only where rows are actually rendered.
 */
export const hasMountsBelow = (path: string, mounts: FileManagerMount[]): boolean => {
  const base = segmentsOf(path);
  return mounts.some((mount) => isMountBelow(base, segmentsOf(mount.prefix)));
};

/**
 * Pure, exported for tests: the children of a path that no mount sits at — answered from the mount
 * table alone, with no Graph call.
 *
 * Forced by the contract rather than chosen: with `prefix` carrying both structure and label (D1), the
 * levels above a mount exist only implicitly. So for every mount below `path`, take the next segment;
 * if that segment completes a mount's prefix the row IS that mount, otherwise it is an intermediate
 * namespace level, deduped because several mounts share it.
 *
 * The project page's depth-1 prefixes produce exactly today's two mount rows at `/` and no namespace
 * rows anywhere — which is why multi-project needed no new concept here, only more depth. A mount at
 * the root contributes nothing at all, for the same reason a mount root has no listing of its own:
 * it sits AT this path rather than below it, so its children come from the drive.
 */
export const syntheticListing = (
  path: string,
  mounts: FileManagerMount[],
): SyntheticItem[] => {
  const base = segmentsOf(path);
  const items: SyntheticItem[] = [];
  const seen = new Set<string>();

  for (const mount of mounts) {
    const prefix = segmentsOf(mount.prefix);
    if (!isMountBelow(base, prefix)) continue;

    const segment = prefix[base.length];
    const childPath = `/${[...base, segment].join('/')}`;
    if (seen.has(childPath)) continue;
    seen.add(childPath);

    // Folder-shaped, but NOT driveItems. hasFolderChildren is optimistic for a mount so the sidebar
    // renders a chevron — useFileManagerTreeRow's known-guess mechanism self-corrects it to false once
    // the mount's own children come back with no subfolders. No childCount (nothing to size a skeleton
    // with), and no size/lastModified*/webUrl, because neither row is a driveItem.
    items.push(
      prefix.length === base.length + 1
        ? {
            type: 'mount',
            name: mountDisplayName(mount),
            path: mount.prefix,
            driveId: mount.driveId,
            id: mount.rootId,
            hasFolderChildren: true,
          }
        : { type: 'namespace', name: segment, path: childPath, hasFolderChildren: true },
    );
  }

  return items;
};

export type FileManagerMountsState = {
  /** The host's table, verbatim. Non-empty and authoritative — see provideFileManagerMounts. */
  mounts: ComputedRef<FileManagerMount[]>;
  resolve: (path: string) => MountTarget | undefined;
  /**
   * The mount a path lives under, undefined outside every mount (the virtual root, an intermediate
   * namespace, or an unknown path). Alongside `resolve` rather than replacing it: the drop policy is
   * derived from the mounts' `audience`, which a MountTarget deliberately does not carry.
   */
  mountAt: (path: string) => FileManagerMount | undefined;
  /**
   * Whether anything backs this path at all — exactly `mountAt(path) !== undefined`, named. The one
   * spelling of that question the state offers, so `mountAt` stays "which mount" and this stays
   * "is there one", rather than every caller re-deriving it from one or the other.
   *
   * NOT named after a drive, and the distinction is the point: what a caller wants to know here is
   * whether some store answers for this path — whether the path bottoms out in real content instead
   * of in the namespace structure above it. A SharePoint drive is what answers today (see `driveId`),
   * so the two questions have the same answer today; they are not the same question, and a mount
   * backed by another source would make that visible. Its readers — the write-side guards canUploadTo
   * and canCreateFolderAt — genuinely mean the general form: "is there something here to write into".
   *
   * `resolve` is deliberately not how this is asked. Testing `resolve(path) !== undefined` gets the
   * same boolean while allocating a MountTarget (and doing the `rest` segment arithmetic) to throw it
   * away, on a predicate that now runs in every useDirectoryChildrenQuery's `enabled`. It also leaves
   * `resolve` meaning only "give me the Graph coordinates", used only where coordinates are consumed.
   */
  hasBackingStore: (path: string) => boolean;
  /**
   * Whether this path is a place the file manager can show at all. The canonical account of that
   * rule, which every other site defers to:
   *
   *   A path is a place if a backing store answers for it, or if the structure above the stores
   *   lists it.
   *
   * Two clauses because the namespace has two kinds of level. Mount prefixes carry both structure and
   * label (D1), so the levels ABOVE a mount exist only implicitly, as the shared leading segments of
   * the table's prefixes — real places with real rows (syntheticListing materializes them) that no
   * store has ever heard of. Anything matching neither clause belongs to nothing and must not be
   * fetched or navigated to.
   *
   * Exists because `resolve()`/`hasBackingStore` cannot answer it: they are false for "no store here"
   * and for "nowhere" alike, so a caller reading either as the latter is silently wrong about the
   * former — it treats every implied level as a dead deep link. That is one predicate away from a
   * pane that cannot open anything but a mount, and it happened: useNavigationPathGuard asked the
   * half-question, so a table whose prefixes are more than one segment deep had every intermediate
   * level `replace`d back to root one tick after the user opened it.
   *
   * So: ask this whenever the question is "may the user be here". Ask `hasBackingStore` when the
   * question is "is there something here to write into", and `resolve` only when the answer is going
   * into a service call.
   */
  isNavigablePath: (path: string) => boolean;
  /**
   * Whether this path is listed from the mount table rather than from a backing store — the virtual
   * root, or an intermediate namespace level. The second clause of isNavigablePath's rule above, on
   * its own, because useDirectoryChildrenQuery has to know WHICH way to list a path and not merely
   * that it may. Distinct from "unknown": a path that is neither this nor backed belongs to nothing.
   */
  isSyntheticPath: (path: string) => boolean;
  /** See syntheticListing. */
  syntheticListing: (path: string) => SyntheticItem[];
};

export const fileManagerMountsKey: InjectionKey<FileManagerMountsState> =
  Symbol('fileManagerMounts');

/**
 * Adapts the host's mount table into the shape the rest of the tree injects. Pure: no query, no
 * probe, no status — those all belong to the host (D4, see `useProjectMounts`), which renders its
 * loading/unprovisioned/unavailable notices *instead of* the file manager.
 *
 * That inversion is what deletes the old `isReady` phase: the table this receives is already settled,
 * so there is no window in which a path resolves to nothing merely because the answer had not arrived
 * yet, and no second readiness flag for `useDirectoryChildrenQuery`/`useNavigationPathGuard` to keep
 * in step with.
 */
export const provideFileManagerMounts = (
  source: MaybeRefOrGetter<FileManagerMount[]>,
) => {
  const mounts = computed<FileManagerMount[]>(() => toValue(source));

  const resolve = (path: string) => resolveMountPath(path, mounts.value);
  const mountAt = (path: string) => matchMountPath(path, mounts.value);
  const listing = (path: string) => syntheticListing(path, mounts.value);
  // matchMountPath, not resolve: same answer (resolveMountPath returns undefined exactly when
  // matchMountPath does — it is the only way it can fail), without allocating a MountTarget to
  // discard. See the interface docstring for why it is not spelled `resolve(path) !== undefined`.
  const hasBackingStore = (path: string) => mountAt(path) !== undefined;
  // The root is synthetic even with nothing under it — it is the one path that exists independently of
  // the table. Any other path qualifies only by having a mount somewhere below it.
  //
  // Unless a mount sits AT the root, in which case the root is an ordinary folder in that store and
  // this is false there. Stated rather than left to call-site ordering: useDirectoryChildrenQuery
  // happens to consult resolve() first and would list correctly either way, but a predicate documented
  // as "listed from the mount table rather than from a backing store" must not answer true for one.
  //
  // That `!hasBackingStore` is also exactly what lets isNavigablePath below carry BOTH root shapes
  // with no root branch of its own: a root with no mount at it is synthetic by this first clause, and
  // a collapseSoleMount root (`prefix: '/'`) is backed, so `hasBackingStore || isSyntheticPath` is
  // true either way. Nobody should "restore" a root special case into isNavigablePath — it would only
  // ever shadow one of the two clauses that already cover it.
  const isSyntheticPath = (path: string) =>
    (path === '/' && !hasBackingStore(path)) || hasMountsBelow(path, mounts.value);
  const isNavigablePath = (path: string) => hasBackingStore(path) || isSyntheticPath(path);

  // Dev-only, because the invariants FileManagerMount documents (unique `key`, unique `prefix`, a root
  // mount being the table's only mount) are terms this file cannot enforce — a violation is a host
  // bug, and every one of them fails SILENTLY: duplicate keys read as one mount so a cross-mount drop
  // is attempted as a same-drive move, and a shadowed or nested prefix simply has no row anywhere.
  // Logged and never thrown: a table with one bad row still browses correctly for every other mount in
  // it, and blanking the whole pane would turn a partial defect into a total one.
  if (import.meta.env.DEV) {
    watch(
      mounts,
      (table) => {
        const duplicated = (values: string[]) => [
          ...new Set(values.filter((value, index) => values.indexOf(value) !== index)),
        ];

        const keys = duplicated(table.map((mount) => mount.key));
        if (keys.length) {
          console.error('[file-manager] duplicate mount keys in the supplied table:', keys);
        }

        const prefixes = duplicated(table.map((mount) => mount.prefix));
        if (prefixes.length) {
          console.error(
            '[file-manager] duplicate mount prefixes — one mount silently shadows another:',
            prefixes,
          );
        }

        // The same defect as a duplicate, one level out: a mount grafted strictly BELOW another's
        // prefix has no row anywhere either. The shallower mount is a backing store at that level, so
        // the level's children come from the store rather than from the table (see syntheticListing,
        // which contributes nothing at or below a mount) — and the store has never heard of the deeper
        // mount. Reachable only by deep link, which is not reachable.
        //
        // Also, for free, the one term of `prefix`'s contract that had no check at all: a root mount
        // MUST be the table's only mount. A root prefix is `[]` segments, and isMountBelow([], x) is
        // true for every non-root x, so any companion mount trips this pair.
        // Split once per mount rather than once per PAIR: this is the only quadratic scan in the file,
        // and the table is expected to reach a few hundred rows.
        const segments = table.map((mount) => segmentsOf(mount.prefix));
        const nested = table.flatMap((mount, index) =>
          table
            .filter((_, other) => isMountBelow(segments[other], segments[index]))
            .map((above) => `${mount.prefix} (under ${above.prefix})`),
        );
        if (nested.length) {
          console.error(
            '[file-manager] nested mount prefixes — the deeper mount has no row anywhere:',
            nested,
          );
        }
      },
      { immediate: true },
    );
  }

  const state: FileManagerMountsState = {
    mounts,
    resolve,
    mountAt,
    hasBackingStore,
    isNavigablePath,
    isSyntheticPath,
    syntheticListing: listing,
  };
  provide(fileManagerMountsKey, state);
  return state;
};

export const useFileManagerMounts = () => {
  const state = inject(fileManagerMountsKey);
  if (!state) {
    throw new Error(
      'useFileManagerMounts() must be called within a component tree provided by provideFileManagerMounts()',
    );
  }
  return state;
};
